import React from 'react';
import PropTypes from 'prop-types';
import { Container, Alert, AlertTitle } from '@mui/material';
import useAuth from '../hooks/useAuth';
import LoadingScreen from '../components/LoadingScreen';

RoleBasedGuard.propTypes = {
  accessibleRoles: PropTypes.array, // Example ['admin', 'leader']
  children: PropTypes.node
};

export default function RoleBasedGuard({ accessibleRoles, children }) {
  const { resiviewUser, resiviewUserResolved, isInitialized, isAuthenticated } = useAuth();

  // The role lives on the ResiView profile, which is fetched separately from and
  // AFTER Firebase auth resolves. Denying while that fetch is in flight showed
  // "Permission Denied" to users who DO have permission — most visibly on a deep
  // link or a hard refresh, where there is no warm state to fall back on.
  //
  // `resiviewUserResolved` is what makes "not back yet" distinguishable from "no
  // profile". It is set on BOTH the success and failure paths, so a failed
  // lookup still reaches the denial below rather than spinning forever.
  const stillResolving = !isInitialized || (isAuthenticated && !resiviewUserResolved);
  if (stillResolving) return <LoadingScreen />;

  // Compare case-insensitively: accessibleRoles arrays are capitalized (e.g. ['Admin'])
  // while roles elsewhere may vary in casing. Deny access when the user's role is NOT
  // in the allowed list.
  const allowed = (accessibleRoles || []).map((role) => role.toLowerCase());
  const userRole = resiviewUser?.role?.toLowerCase();

  if (!resiviewUser || !allowed.includes(userRole)) {
    return (
      <Container>
        <Alert severity="error">
          <AlertTitle>Permission Denied</AlertTitle>
          You do not have permission to access this page
        </Alert>
      </Container>
    );
  }

  return <>{children}</>;
}
